import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ContentModal } from "../UnifiedModal";
import SocialShareButtons from "../SocialShareButtons";
import {
  faGlobe,
  faLock,
  faCopy,
  faCheck,
  faArrowRight,
  faShieldAlt,
  faCode,
} from "@fortawesome/free-solid-svg-icons";
import "./ShareStrategyModal.css";

const ShareStrategyModal = ({
  show,
  onHide,
  strategyName,
  isPublic,
  shareUrl,
  onMakePublic,
  onMakePrivate,
  isInMarketplace = false,
  marketplaceListingId,
  isOwner = true,
  existingPrivateShare,
  shareInfoLoading = false,
  onRefreshShareInfo,
}) => {
  const [copied, setCopied] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareType, setShareType] = useState(null);
  const [privateShareCreated, setPrivateShareCreated] = useState(false);
  const [privateShareUrl, setPrivateShareUrl] = useState("");
  const [forceRegenerate, setForceRegenerate] = useState(false);
  // Track when we just published to marketplace (before prop updates)
  const [justPublished, setJustPublished] = useState(false);
  const [justPublishedListingId, setJustPublishedListingId] = useState(null);
  // Track when strategy was just made private (unpublished)
  const [justMadePrivate, setJustMadePrivate] = useState(false);
  // Protect code toggle - when true, users can run/deploy but cannot view/copy code
  const [protectCode, setProtectCode] = useState(false);

  // Reset state when modal opens or closes
  React.useEffect(() => {
    if (!show) {
      // Reset everything when modal closes
      setCopied(false);
      setShareType(null);
      setIsToggling(false); // Reset loading state
      setShareError("");
      setPrivateShareCreated(false);
      setPrivateShareUrl("");
      setForceRegenerate(false);
      setJustPublished(false);
      setJustPublishedListingId(null);
      setJustMadePrivate(false);
      setProtectCode(false);
    } else {
      // Every owner entry point opens the same two-choice flow. Existing status
      // is shown on each choice so the user never has to remember what they did.
      setShareType(isInMarketplace ? "public" : null);
      setIsToggling(false);
      setShareError("");
      setForceRegenerate(false);
      setProtectCode(false);
    }
  }, [show, isInMarketplace]);

  const copyUrlToClipboard = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  const handleCopyUrl = () => {
    const urlToCopy = privateShareCreated
      ? privateShareUrl
      : shareType === "private" && existingPrivateShare?.shareUrl
        ? existingPrivateShare.shareUrl
        : shareUrl;
    copyUrlToClipboard(urlToCopy);
  };

  const handleToggleVisibility = async (shouldForceRegenerate = false) => {
    setIsToggling(true);
    setShareError("");
    try {
      // Zoom-style: No password required - backend auto-generates secure token
      // For public shares, pass allowClone based on protectCode toggle
      const options = {
        forceRegenerate: shouldForceRegenerate || forceRegenerate,
        allowClone: !protectCode,
      };
      const result = await onMakePublic(undefined, shareType, options);

      if (result === false) {
        setIsToggling(false);
        setShareError("Sharing could not be updated. Please try again.");
        return;
      }

      // Check if this was a private share creation
      if (result?.isPrivate && result?.success && result?.shareUrl) {
        // Private share created - show success state with auto-generated link
        setPrivateShareCreated(true);
        setPrivateShareUrl(result.shareUrl);
        setIsToggling(false);
        if (onRefreshShareInfo) {
          onRefreshShareInfo();
        }
      } else if (shareType === "private" && result?.shareUrl) {
        // Alternative check for private shares
        setPrivateShareCreated(true);
        setPrivateShareUrl(result.shareUrl);
        setIsToggling(false);
      } else if (
        result?.success &&
        result?.marketplaceListingId &&
        (result?.moderationStatus === "approved" || result?.isPublic === true)
      ) {
        const listingId = result.marketplaceListingId || marketplaceListingId;
        setJustPublished(true);
        setJustPublishedListingId(listingId || null);
        setIsToggling(false);
        if (onRefreshShareInfo) {
          onRefreshShareInfo();
        }
      } else {
        // Fallback - reset loading state
        setIsToggling(false);
      }
    } catch (error) {
      // Error is handled by parent component with toast
      console.error("[ShareStrategyModal] Error:", error);
      setIsToggling(false);
      setShareError(error?.message || "Sharing could not be updated. Please try again.");
      return false;
    }
  };

  const handleMakePrivate = async () => {
    // CRITICAL: onMakePrivate MUST be provided - no fallback allowed
    // Previous fallback to onMakePublic caused silent bugs where clicking
    // The removal action would publish instead of unpublish
    if (!onMakePrivate) {
      console.error(
        "[ShareStrategyModal] CRITICAL: onMakePrivate prop is required but not provided!",
      );
      alert(
        "Unable to make strategy private - configuration error. Please report this bug.",
      );
      return false;
    }

    setIsToggling(true);
    setShareError("");
    try {
      const result = await onMakePrivate();
      if (result === false) {
        // Error already handled by parent component with toast
        setIsToggling(false);
        return;
      }
      // Success - show success state instead of closing modal
      setIsToggling(false);
      setJustMadePrivate(true);
    } catch (error) {
      // Error is handled by parent component with toast
      console.error("[ShareStrategyModal] Error during makePrivate:", error);
      setIsToggling(false);
      setShareError(error?.message || "The Marketplace listing could not be removed. Please try again.");
      return false;
    }
  };

  return (
    <ContentModal
      isOpen={show}
      onClose={onHide}
      size="md"
      className="share-strategy-modal"
      title="Share Strategy"
      ariaLabel="Share Strategy"
      zIndex={10050}
    >
      {!isOwner ? (
        <>
          {/* Non-owner view - show marketplace link + social share (no publish/unpublish) */}
          <div className="share-link-section">
            <div className="strategy-header">
              <div className="strategy-name">{strategyName}</div>
              <div className="strategy-subtitle">
                Share this strategy with others
              </div>
            </div>

            <div className="share-link-container">
              <label className="link-label">Marketplace link</label>
              <div className="link-input-group">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="link-input"
                />
                <button
                  className={`copy-btn ${copied ? "copied" : ""}`}
                  onClick={handleCopyUrl}
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                  <span>{copied ? "Copied" : "Copy"}</span>
                </button>
              </div>
            </div>

            {/* Social share buttons for non-owners */}
            {shareUrl && (
              <SocialShareButtons
                url={shareUrl}
                text={`Check out "${strategyName}" on BotSpot Marketplace!`}
                className="compact"
              />
            )}

            <div className="share-info">
              <FontAwesomeIcon icon={faGlobe} className="me-2" />
              <span className="text-muted">
                This strategy is available in the marketplace
              </span>
            </div>
          </div>
        </>
      ) : justMadePrivate ? (
        <>
          {/* Success State - Just Made Private - Clean text only */}
          <div className="private-removed-state">
            <h5 className="private-removed-title">Removed from Marketplace</h5>
            <p className="private-removed-desc">
              <strong>"{strategyName}"</strong> is now private and no longer
              visible to other users.
            </p>
          </div>

          {/* Action button */}
          <div className="modal-action-buttons">
            <Button variant="primary" onClick={onHide} size="sm">
              Done
            </Button>
          </div>
        </>
      ) : (isInMarketplace && shareType !== "private") || justPublished || privateShareCreated ? (
        <>
          {isInMarketplace && !justPublished && !privateShareCreated && (
            <div className="share-type-pills">
              <button
                type="button"
                className="share-pill active"
                onClick={() => setShareType("public")}
              >
                <FontAwesomeIcon icon={faGlobe} className="pill-icon" />
                <span className="pill-label">Marketplace</span>
                <span className="share-pill-status">Shared</span>
              </button>
              <button
                type="button"
                className="share-pill"
                onClick={() => setShareType("private")}
              >
                <FontAwesomeIcon icon={faLock} className="pill-icon" />
                <span className="pill-label">Private Link</span>
                {existingPrivateShare?.shareUrl && (
                  <span className="share-pill-status">Link ready</span>
                )}
              </button>
            </div>
          )}
          {/* Success State */}
          <div className="success-state">
            {/* Marketplace success */}
            {(isInMarketplace || justPublished) && !privateShareCreated && (
              <>
                <div className="marketplace-success-status" role="status">
                  <span className="marketplace-success-status-icon" aria-hidden="true">
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  <span>Marketplace listing active</span>
                </div>
                <h5 className="success-title">You are now shared to the Marketplace.</h5>
                <p className="success-desc">
                  "{strategyName}" is now available in the Marketplace.
                </p>
                {(marketplaceListingId || justPublishedListingId) && (
                  <a
                    href={`/marketplace/strategy/${marketplaceListingId || justPublishedListingId}`}
                    className="marketplace-view-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View your listing
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </a>
                )}
              </>
            )}
            {/* Private share success - simple and clean */}
            {privateShareCreated && (
              <>
                <div className="success-icon">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <h5 className="success-title">Link Created</h5>
                <p className="success-desc">
                  Share this link with anyone you want to give access to "
                  {strategyName}"
                </p>
              </>
            )}
          </div>

          {/* Share Link - Only for private shares */}
          {privateShareCreated && (
            <div className="private-share-success">
              <div className="private-link-box">
                <input
                  type="text"
                  value={privateShareUrl}
                  readOnly
                  className="private-link-input"
                  onClick={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className={`copy-btn-inline ${copied ? "copied" : ""}`}
                  onClick={handleCopyUrl}
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <SocialShareButtons
                url={privateShareUrl}
                text={`Check out "${strategyName}" on BotSpot!`}
                className="compact"
              />
              <div className="modal-action-buttons">
                <Button variant="secondary" onClick={onHide} size="sm">
                  Done
                </Button>
              </div>
            </div>
          )}

          {/* Social Share & Actions for marketplace */}
          {!privateShareCreated && (
            <div className="marketplace-footer">
              <SocialShareButtons
                url={`${window.location.origin}/marketplace/strategy/${marketplaceListingId || justPublishedListingId}`}
                text={`Check out my trading strategy "${strategyName}" on BotSpot!`}
                label="Share your listing"
                className="marketplace-share-compact"
              />
              {shareError && (
                <div className="error-message" role="alert">
                  {shareError}
                </div>
              )}
              <div className="modal-action-buttons">
                <Button
                  variant="link"
                  className="marketplace-remove-listing"
                  onClick={handleMakePrivate}
                  disabled={isToggling}
                  size="sm"
                >
                  {isToggling ? "Removing..." : "Remove listing"}
                </Button>
                <Button
                  variant="primary"
                  className="marketplace-done-button"
                  onClick={onHide}
                  size="sm"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Strategy Name Header - Elegant */}
          <div className="strategy-header">
            <div className="strategy-name">{strategyName}</div>
            <div className="strategy-subtitle">
              Choose how to share
            </div>
          </div>

          <div className="share-type-pills">
              <button
                type="button"
                className={`share-pill ${shareType === "public" ? "active" : ""}`}
                onClick={() => setShareType("public")}
              >
                <FontAwesomeIcon icon={faGlobe} className="pill-icon" />
                <span className="pill-label">Marketplace</span>
                {isInMarketplace && <span className="share-pill-status">Shared</span>}
              </button>
              <button
                type="button"
                className={`share-pill ${shareType === "private" ? "active" : ""}`}
                onClick={() => setShareType("private")}
              >
                <FontAwesomeIcon icon={faLock} className="pill-icon" />
                <span className="pill-label">Private Link</span>
                {existingPrivateShare?.shareUrl && (
                  <span className="share-pill-status">Link ready</span>
                )}
              </button>
          </div>

          {shareType === "private" && (
            <div className="share-private-context">
              {shareInfoLoading ? (
                <div className="share-loading">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="ms-2">Loading...</span>
                </div>
              ) : existingPrivateShare?.shareUrl ? (
                <>
                  <div className="existing-link-section">
                    <div className="existing-link-header">
                      <span className="existing-link-label">
                        Current private link
                      </span>
                      <button
                        type="button"
                        className={`copy-icon-btn ${copied ? "copied" : ""}`}
                        onClick={() =>
                          copyUrlToClipboard(existingPrivateShare.shareUrl)
                        }
                        title={copied ? "Copied!" : "Copy link"}
                      >
                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={existingPrivateShare.shareUrl}
                      readOnly
                      className="private-link-input"
                      onClick={(e) => e.target.select()}
                    />
                  </div>
                  <SocialShareButtons
                    url={existingPrivateShare.shareUrl}
                    text={`Check out "${strategyName}" on BotSpot!`}
                    className="compact"
                  />
                </>
              ) : null}
            </div>
          )}

          {/* Explanation - Only show after selecting share type */}
          {shareType && (
            <div className="share-explanation">
              {shareType === "private" ? (
                <p>
                  Generate a secure private link that only people you share it
                  with can access. Your strategy will <strong>not</strong>{" "}
                  appear in the marketplace.
                </p>
              ) : (
                <p>
                  {isInMarketplace
                    ? "This strategy is already available in the Marketplace."
                    : protectCode
                      ? "Share your strategy in the Marketplace right away without exposing its source code."
                      : "Share your strategy in the Marketplace. Other users can discover and use it right away."}
                </p>
              )}
            </div>
          )}

          {shareType === "public" && isInMarketplace && marketplaceListingId && (
            <a
              href={`/marketplace/strategy/${marketplaceListingId}`}
              className="marketplace-view-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              View your listing
              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </a>
          )}

          {/* Code Protection Toggle - Only for regular public shares. Listings are protected by default. */}
          {shareType === "public" && !isInMarketplace && (
            <div className="protect-code-section">
              <div className="protect-code-toggle">
                <Form.Check
                  type="switch"
                  id="protect-code-toggle"
                  checked={protectCode}
                  onChange={(e) => setProtectCode(e.target.checked)}
                  label={
                    <span className="protect-label">
                      <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                      Protect my code
                    </span>
                  }
                />
              </div>
              <div
                className={`protect-code-info ${protectCode ? "protected" : ""}`}
              >
                {protectCode ? (
                  <>
                    <FontAwesomeIcon
                      icon={faLock}
                      className="info-icon protected"
                    />
                    <span>
                      Users can <strong>run and deploy</strong> your strategy,
                      but <strong>cannot view or copy</strong> the code.
                    </span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCode} className="info-icon" />
                    <span>
                      Users can view, copy, and modify your code freely.
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {shareError && (
            <div className="error-message" role="alert">
              {shareError}
            </div>
          )}
          <div className="modal-action-buttons">
            <Button
              variant="primary"
              onClick={() => {
                // Pass true directly if regenerating (don't rely on async state)
                const shouldRegenerate =
                  shareType === "private" && !!existingPrivateShare?.shareUrl;
                handleToggleVisibility(shouldRegenerate);
              }}
              disabled={
                isToggling ||
                !shareType ||
                (isInMarketplace && isPublic && shareType === "public")
              }
              size="sm"
            >
              {isToggling
                ? "Working..."
                : !shareType
                  ? "Choose an option above"
                  : shareType === "private"
                    ? existingPrivateShare?.shareUrl
                      ? "Regenerate Link"
                      : "Generate Link"
                    : isInMarketplace && isPublic
                      ? "Already Published"
                      : "Share to Marketplace"}
            </Button>
            <Button variant="secondary" onClick={onHide} size="sm">
              Cancel
            </Button>
          </div>
        </>
      )}
    </ContentModal>
  );
};

export default ShareStrategyModal;
